import styled from 'styled-components';
import { color, font, layout, mixin, radius, shadow, space, toneColor } from '../admin_classic_theme';

const PageShell = styled.main`
    min-height: calc(100vh - 90px);
    padding: ${layout.pagePadding};
    background: ${color.page};
    color: ${color.text};

    @media (max-width: 720px) {
        padding: ${layout.pagePaddingSmall};
    }
`;
const PageHeader = styled.header`
    max-width: ${layout.maxWidth};
    margin: 0 auto ${space.lg};
    display: flex;
    justify-content: space-between;
    gap: ${space.xl};
    align-items: flex-end;

    @media (max-width: 820px) {
        align-items: stretch;
        flex-direction: column;
        gap: ${space.md};
    }
`;
const HeaderCopy = styled.div`min-width: 0;`;
const PageTitle = styled.h1`
    margin: 0;
    color: ${color.textStrong};
    ${font.pageTitle};
`;
const PageDescription = styled.p`
    max-width: 680px;
    margin: ${space.sm} 0 0;
    color: ${color.textMuted};
    ${font.caption};
`;
// 얇은 텍스트 탭 줄 — 상단 서브메뉴와 같은 조작 감각을 유지한다.
const PlacementTabs = styled.div`
    max-width: ${layout.maxWidth};
    margin: 0 auto ${space.lg};
    display: flex;
    gap: ${space.xs};
    border-bottom: 1px solid ${color.line};
    overflow-x: auto;
`;
const PlacementTab = styled.button`
    flex: 0 0 auto;
    height: 44px;
    padding: 0 ${space.lg};
    display: inline-flex;
    align-items: center;
    gap: ${space.sm};
    border: 0;
    border-bottom: 2px solid ${props => props.$active ? color.accent : 'transparent'};
    background: transparent;
    color: ${props => props.$active ? color.accent : color.textMuted};
    font-size: 0.95rem;
    font-weight: ${props => props.$active ? 700 : 500};
    white-space: nowrap;
    cursor: pointer;
    transition: color 0.2s, border-color 0.2s;

    &:hover { color: ${color.accent}; }
    ${mixin.focusRing};
`;
const TabLabel = styled.span`
    display: inline-block;
`;
const TabStatus = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${props => toneColor(props.$tone)};

    i { ${mixin.dot}; }
`;
const StatusMessage = styled.div`
    max-width: ${layout.maxWidth};
    margin: 0 auto ${space.lg};
    padding: ${space.md} ${space.lg};
    border: 1px solid ${props => props.$error ? color.danger : color.line};
    border-radius: ${radius.field};
    background: ${color.surface};
    color: ${props => props.$error ? color.danger : color.text};
    ${font.caption};
    font-weight: 600;
`;
const ContentGrid = styled.div`
    max-width: ${layout.maxWidth};
    margin: 0 auto;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(300px, 340px);
    gap: ${space.lg};
    align-items: start;

    @media (max-width: 1020px) { grid-template-columns: 1fr; }
`;
const BaseCard = styled.section`
    ${mixin.panel};
    min-width: 0;
`;
const SettingsCard = styled(BaseCard)`
    padding: ${space.xl};

    @media (max-width: 720px) { padding: ${space.lg}; }
`;
const PreviewCard = styled(BaseCard)`
    position: sticky;
    top: ${space.lg};
    padding: ${space.lg};

    @media (max-width: 1020px) { position: static; }
`;
const SectionHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: ${space.lg};
    margin-bottom: ${space.lg};

    > div { min-width: 0; }
`;
const SectionTitle = styled.h2`
    margin: 0;
    color: ${color.textStrong};
    ${font.sectionTitle};
`;
const SectionDescription = styled.p`
    margin: 5px 0 0;
    color: ${color.textMuted};
    ${font.caption};
`;
const SwitchRow = styled.div`
    flex: 0 0 auto;
    display: flex;
    gap: ${space.md};
    align-items: center;
`;
const SwitchCopy = styled.span`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;

    strong { color: ${color.text}; font-size: 0.8rem; font-weight: 700; }
    span { color: ${color.textFaint}; font-size: 0.75rem; }
`;
const SwitchButton = styled.button`
    ${mixin.switchTrack};
    background: ${props => props.$checked ? color.accent : color.surfaceMuted};
    border-color: ${props => props.$checked ? color.accent : color.lineStrong};

    &:disabled { cursor: not-allowed; opacity: 0.55; }
    &:focus-visible { outline: 2px solid ${color.accent}; outline-offset: 2px; }
`;
const SwitchKnob = styled.span`
    ${mixin.switchKnob};
    left: ${props => props.$checked ? '23px' : '3px'};
    background: ${props => props.$checked ? color.onAccent : color.lineStrong};
`;
const FormSection = styled.div`
    margin-top: ${space.xl};
    padding-top: ${space.xl};
    ${mixin.hairlineTop};

    &:first-of-type { margin-top: 0; padding-top: 0; border-top: 0; }
`;
const SubsectionHeading = styled.div`
    margin-bottom: ${space.lg};

    > div { min-width: 0; }
`;
const SubsectionTitle = styled.h3`
    margin: 0;
    color: ${color.textStrong};
    ${font.label};
    font-size: 1rem;
    font-weight: 700;
`;
const SubsectionDescription = styled.p`
    margin: 4px 0 0;
    color: ${color.textFaint};
    ${font.caption};
`;
const FieldGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${space.sm};
    margin-top: ${space.lg};

    &:first-of-type { margin-top: 0; }
`;
const FieldLabel = styled.label`
    color: ${color.textMuted};
    ${font.label};
`;
const Required = styled.span`
    margin-left: 5px;
    color: ${color.danger};
    font-size: 0.7rem;
    font-weight: 700;
`;
const Optional = styled.span`
    margin-left: 5px;
    color: ${color.textFaint};
    font-size: 0.7rem;
    font-weight: 500;
`;
const TypeOptions = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: ${space.sm};

    @media (max-width: 620px) { grid-template-columns: 1fr; }
`;
const TypeButton = styled.button`
    min-height: 62px;
    padding: ${space.md};
    border: 1px solid ${props => props.$selected ? color.accent : color.lineStrong};
    border-radius: ${radius.field};
    background: ${props => props.$selected ? color.accentSoft : color.surface};
    color: ${props => props.$selected ? color.accent : color.text};
    cursor: pointer;
    text-align: left;
    transition: border-color 0.2s, color 0.2s, background-color 0.2s;

    strong { display: block; font-size: 0.85rem; font-weight: 700; }
    small { display: block; margin-top: 4px; color: ${color.textFaint}; font-size: 0.7rem; line-height: 1.45; }

    &:hover { border-color: ${color.accent}; }
    ${mixin.focusRing};
`;
const TextInput = styled.input`
    ${mixin.field};
`;
const TextArea = styled.textarea`
    ${mixin.field};
    min-height: 130px;
    padding: 10px 12px;
    line-height: 1.65;
    resize: vertical;
`;
const FileInput = styled.input`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
`;
const UploadArea = styled.div`
    min-height: 66px;
    padding: ${space.md} ${space.lg};
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${space.lg};
    border: 1px dashed ${color.lineStrong};
    border-radius: ${radius.field};
    background: ${color.surfaceMuted};

    @media (max-width: 520px) { align-items: stretch; flex-direction: column; }
`;
const UploadCopy = styled.div`
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: ${space.xs};

    strong { overflow: hidden; color: ${color.text}; font-size: 0.8rem; font-weight: 700; text-overflow: ellipsis; white-space: nowrap; }
    span { color: ${color.textFaint}; font-size: 0.75rem; line-height: 1.5; }
`;
const UploadButton = styled.button`
    ${mixin.buttonBase};
    ${mixin.buttonGhost};
    flex: 0 0 auto;
    height: 34px;
    padding: 0 12px;
    font-size: 0.8rem;
`;
const FieldMetaRow = styled.div`
    display: flex;
    justify-content: space-between;
    gap: ${space.lg};
    align-items: flex-start;
`;
const FieldHint = styled.span`
    color: ${color.textFaint};
    font-size: 0.75rem;
    line-height: 1.5;
`;
const CharacterCount = styled.span`
    flex: 0 0 auto;
    color: ${props => props.$nearLimit ? color.danger : color.textFaint};
    font-size: 0.75rem;
    font-variant-numeric: tabular-nums;
`;
const DateGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${space.lg};

    ${FieldGroup} { margin-top: 0; }
    @media (max-width: 620px) { grid-template-columns: 1fr; }
`;
const StatusDot = styled.span`
    ${mixin.dot};
    width: 8px;
    height: 8px;
    color: ${props => toneColor(props.$tone)};
`;
const ScheduleStrip = styled.div`
    margin-top: ${space.lg};
    padding: ${space.md} ${space.lg};
    display: flex;
    align-items: center;
    gap: ${space.md};
    border: 1px solid ${color.line};
    border-radius: ${radius.field};
    background: ${color.surfaceMuted};

    div { min-width: 0; display: flex; flex-direction: column; gap: 3px; }
    strong { color: ${props => toneColor(props.$tone)}; font-size: 0.8rem; font-weight: 700; }
    span { color: ${color.textMuted}; font-size: 0.75rem; line-height: 1.5; }
    small { margin-left: auto; color: ${color.textFaint}; font-size: 0.7rem; white-space: nowrap; }

    @media (max-width: 520px) {
        align-items: flex-start;
        small { display: none; }
    }
`;
const CardFooter = styled.div`
    margin-top: ${space.xl};
    padding-top: ${space.lg};
    ${mixin.hairlineTop};
    display: flex;
    justify-content: space-between;
    gap: ${space.lg};
    align-items: center;

    @media (max-width: 620px) { align-items: stretch; flex-direction: column; }
`;
const FooterMeta = styled.div`
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: ${space.xs};
`;
const SaveState = styled.span`
    color: ${props => props.$dirty ? color.warning : color.success};
    font-size: 0.75rem;
    font-weight: 700;
`;
const UpdatedInfo = styled.span`
    color: ${color.textFaint};
    font-size: 0.75rem;
    line-height: 1.5;
`;
const FooterActions = styled.div`
    display: flex;
    gap: ${space.sm};
    flex: 0 0 auto;
`;
const PrimaryButton = styled.button`
    ${mixin.buttonBase};
    ${mixin.buttonPrimary};
    min-width: 112px;
`;
const ResetButton = styled.button`
    ${mixin.buttonBase};
    ${mixin.buttonGhost};
`;
const SecondaryButton = styled.button`
    ${mixin.buttonBase};
    ${mixin.buttonGhost};
    height: 36px;
`;
const StatePanel = styled.div`
    ${mixin.panel};
    max-width: 720px;
    margin: 56px auto;
    padding: ${space.xl};
    border-left: ${props => props.$error ? `3px solid ${color.danger}` : '0'};
    color: ${color.textMuted};
    ${font.body};
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${space.md};

    strong { color: ${color.textStrong}; ${font.sectionTitle}; }
`;
const VisibilityBadge = styled.span`
    ${mixin.statusChip};
    color: ${props => toneColor(props.$tone)};

    i { ${mixin.dot}; }
`;
// $compact: 가입혜택 탭의 통합 카드 안에 들어갈 때 높이를 줄인다.
const PreviewStage = styled.div`
    min-height: ${props => props.$compact ? '400px' : '500px'};
    padding: ${space.lg} ${space.md};
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid ${color.line};
    border-radius: ${radius.field};
    background: ${color.surfaceMuted};
`;
const PreviewViewport = styled.div`
    width: min(${props => props.$compact ? '270px' : '320px'}, 100%);
    overflow: hidden;
    border: 1px solid ${color.lineStrong};
    border-radius: ${radius.control};
    background: ${color.surface};
`;
const PreviewBar = styled.div`
    min-height: 40px;
    padding: 0 ${space.md};
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${space.md};
    border-bottom: 1px solid ${color.line};
    background: ${color.surface};
    color: ${color.textFaint};
    font-size: 0.7rem;
`;
const PreviewBrand = styled.strong`
    display: inline-flex;
    align-items: center;
    gap: 7px;
    color: ${color.text};
    font-size: 0.75rem;
    font-weight: 700;

    i { width: 8px; height: 8px; border-radius: 50%; background: ${color.accent}; }
`;
const PreviewCanvas = styled.div`
    min-height: ${props => props.$compact ? '330px' : '410px'};
    padding: ${space.lg} ${space.md};
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${color.surfaceMuted};
`;
const PopupPreview = styled.div`
    width: min(260px, 100%);
    max-height: 380px;
    overflow: auto;
    border-radius: ${radius.control};
    background: ${color.surface};
    box-shadow: ${shadow.raised};
    text-align: center;
`;
const PreviewImage = styled.img`
    display: block;
    width: 100%;
    max-height: 200px;
    object-fit: contain;
    background: ${color.surfaceMuted};
`;
const ImagePlaceholder = styled.div`
    min-height: 130px;
    padding: ${space.lg};
    display: flex;
    align-items: center;
    justify-content: center;
    border-bottom: 1px solid ${color.line};
    background: ${color.surfaceMuted};
    color: ${color.textFaint};
    font-size: 0.75rem;
    line-height: 1.7;
`;
const PreviewTitle = styled.h3`
    margin: ${space.lg} ${space.lg} ${space.sm};
    color: ${color.textStrong};
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.4;
    overflow-wrap: anywhere;
`;
const PreviewBody = styled.p`
    margin: ${space.sm} ${space.lg} ${space.lg};
    color: ${color.textMuted};
    font-size: 0.85rem;
    line-height: 1.7;
    white-space: pre-wrap;
    overflow-wrap: anywhere;

    a { color: ${color.accent}; text-decoration: underline; text-underline-offset: 2px; }
`;
const PreviewAction = styled.button`
    width: 100%;
    height: 44px;
    border: 0;
    border-top: 1px solid ${color.line};
    background: ${color.surface};
    color: ${color.accent};
    font-size: 0.85rem;
    font-weight: 700;
    cursor: pointer;
`;
const PreviewNote = styled.p`
    margin: ${space.md} 0 0;
    color: ${color.textFaint};
    font-size: 0.75rem;
    line-height: 1.6;

    strong { display: block; margin-bottom: 3px; color: ${color.text}; font-size: 0.75rem; font-weight: 700; }
`;

export {
    PageShell,
    PageHeader,
    HeaderCopy,
    PageTitle,
    PageDescription,
    PlacementTabs,
    PlacementTab,
    TabStatus,
    TabLabel,
    StatusMessage,
    ContentGrid,
    SettingsCard,
    PreviewCard,
    SectionHeader,
    SectionTitle,
    SectionDescription,
    SwitchRow,
    SwitchCopy,
    SwitchButton,
    SwitchKnob,
    FormSection,
    SubsectionHeading,
    SubsectionTitle,
    SubsectionDescription,
    FieldGroup,
    FieldLabel,
    Required,
    Optional,
    TypeOptions,
    TypeButton,
    TextInput,
    TextArea,
    FileInput,
    UploadArea,
    UploadCopy,
    UploadButton,
    FieldMetaRow,
    FieldHint,
    CharacterCount,
    DateGrid,
    StatusDot,
    ScheduleStrip,
    CardFooter,
    FooterMeta,
    SaveState,
    UpdatedInfo,
    FooterActions,
    PrimaryButton,
    ResetButton,
    SecondaryButton,
    StatePanel,
    VisibilityBadge,
    PreviewStage,
    PreviewViewport,
    PreviewBar,
    PreviewBrand,
    PreviewCanvas,
    PopupPreview,
    PreviewImage,
    ImagePlaceholder,
    PreviewTitle,
    PreviewBody,
    PreviewAction,
    PreviewNote
};
